import React, { useState, useContext } from "react";
import { X, Search } from "lucide-react";
import Modal from "react-modal";
import { Button } from "Components/Button";
import Input from "Components/Form/Input";
import Select from "Components/Form/Select";
import { Product } from "Hooks/useProducts/interface";
import { useProductCombos } from "Hooks/useProductCombos";
import { CATEGORIES } from "Constants/Categories";
import { createToast } from "Utils/toastFunc";
import { LoadingText } from "Components/LoadingText";
import { useProductList } from "../../hooks/useProductList";
import { ItemsContext } from "../../Provider";
import { ProductFromAPI, ProductWithSource } from "../../interface";

interface UnifiedProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "search" | "replacement";
  // Para modo de busca
  onProductSelect?: (product: Product) => void;
  multiple?: boolean;
  selectedProducts?: Product[];
  onMultipleSelect?: (products: Product[]) => void;
  // Para modo de substituição
  originalProduct?: ProductWithSource;
  onConfirmReplacement?: (newProducts: ProductWithSource[]) => void;
}

export const UnifiedProductModal: React.FC<UnifiedProductModalProps> = ({
  isOpen,
  onClose,
  mode,
  onProductSelect,
  multiple = false,
  selectedProducts = [],
  onMultipleSelect,
  originalProduct,
  onConfirmReplacement,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [localSelectedProducts, setLocalSelectedProducts] =
    useState<Product[]>(selectedProducts);
  const [filters, setFilters] = useState({
    category: "",
    services: "",
    subcategory: "",
  });
  const [hasSearched, setHasSearched] = useState(false);

  // Contexto do carrinho
  const cartContext = useContext(ItemsContext);

  const {
    productsList,
    loadingProducts,
    getProducts,
    fetchNewPage,
    pageSelected,
    productsPagination,
  } = useProductList();

  // Hook para buscar combos de produtos (categorias, status, etc.)
  const { combos } = useProductCombos({
    includeCombo: ["categories", "subcategories", "status", "services"],
    searchBudget: true,
  });

  const { categories, subcategories } = combos ?? {};

  // Filtrar categorias baseado nas constantes do projeto
  const categoriesOpts = categories?.filter((category) =>
    [
      CATEGORIES.exam,
      CATEGORIES.checkup,
      CATEGORIES.test,
      CATEGORIES.consult,
    ].includes(category.id)
  );

  const subcategoriesOpts = subcategories?.filter(
    (subcategory) => subcategory.category_id === Number(filters.category)
  );

  // Verificar se produto já está no carrinho
  const isProductInCart = (productId: number) => {
    if (!cartContext) return false;
    return cartContext.items.some((item) => item.product_id === productId);
  };

  // Verificar se produto já está selecionado
  const isProductSelected = (productId: number) => {
    if (mode === "replacement") {
      return localSelectedProducts.some((product) => product.id === productId);
    }
    return localSelectedProducts.some((p) => p.id === productId);
  };

  const handleFilterChange = (filterName: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: "",
      services: "",
      subcategory: "",
    });
    setSearchTerm("");
    setHasSearched(false);
  };

  const applyFilters = () => {
    const searchParams = {
      search: searchTerm,
      categories: filters.category || "1,2,3",
      services: filters.services,
      subcategory: filters.subcategory,
    };
    getProducts(searchParams);
    setHasSearched(true);
  };

  const handleSearch = () => {
    applyFilters();
  };

  // Função para adicionar produto ao carrinho (modo busca)
  const handleAddToCart = (product: Product | ProductFromAPI) => {
    if (!cartContext) {
      createToast("error", "Erro ao acessar o carrinho");
      return;
    }

    if (isProductInCart(product.id)) {
      createToast("warning", "Este produto já está no carrinho");
      return;
    }

    // Converter Product ou ProductFromAPI para o formato do carrinho
    const cartProduct = {
      product_id: product.id,
      product_name: product.product || "Produto não identificado",
      product_code: product.sku || "",
      amount: 1,
      product_price: (() => {
        if ("default_partner" in product && product.default_partner?.price) {
          return product.default_partner.price;
        }
        if (product.partners && product.partners.length > 0) {
          return product.partners[0].price;
        }
        return 0;
      })(),
      source: "manual" as const,
    };

    cartContext.addItem(cartProduct);
    createToast("success", "Produto adicionado ao carrinho!");
  };

  const handleProductSelect = (product: Product | ProductFromAPI) => {
    const isSingleSearchWithCallback =
      mode !== "replacement" && !multiple && Boolean(onProductSelect);

    if (isSingleSearchWithCallback && isProductInCart(product.id)) {
      return;
    }

    if (mode === "replacement") {
      // Modo substituição: adicionar/remover da seleção
      if (isProductSelected(product.id)) {
        setLocalSelectedProducts((prev) =>
          prev.filter((p) => p.id !== product.id)
        );
      } else {
        setLocalSelectedProducts((prev) => [...prev, product as Product]);
      }
    } else if (multiple) {
      // Modo busca - seleção múltipla
      const isAlreadySelected = localSelectedProducts.some(
        (p) => p.id === product.id
      );
      if (isAlreadySelected) {
        const newSelection = localSelectedProducts.filter(
          (p) => p.id !== product.id
        );
        setLocalSelectedProducts(newSelection);
      } else {
        const newSelection = [...localSelectedProducts, product as Product];
        setLocalSelectedProducts(newSelection);
      }
    } else if (onProductSelect) {
      // Modo busca - seleção única
      // Usar a função passada pelo componente pai (que inclui addManualProduct)
      onProductSelect(product as Product);
    } else {
      // Fallback para a função interna (sem tracking de produtos manuais)
      handleAddToCart(product as Product);
    }
  };

  const handleConfirmReplacement = () => {
    if (localSelectedProducts.length === 0) {
      createToast(
        "warning",
        "Selecione pelo menos um produto para substituição"
      );
      return;
    }

    if (onConfirmReplacement) {
      // Converter Product para ProductWithSource
      const newProducts: ProductWithSource[] = localSelectedProducts.map(
        (product) => ({
          product_id: product.id,
          product_name: product.product || "Produto não identificado",
          product_code: product.sku || "",
          amount: 1,
          product_price: (() => {
            if (
              "default_partner" in product &&
              product.default_partner?.price
            ) {
              return product.default_partner.price;
            }
            if (product.partners && product.partners.length > 0) {
              return product.partners[0].price;
            }
            return 0;
          })(),
          source: "manual",
        })
      );

      onConfirmReplacement(newProducts);
      onClose();
      setLocalSelectedProducts([]);
    }
  };

  const handleConfirmMultipleSelection = () => {
    if (onMultipleSelect) {
      onMultipleSelect(localSelectedProducts);
      onClose();
    }
  };

  const handleClose = () => {
    setFilters({
      category: "",
      services: "",
      subcategory: "",
    });
    setSearchTerm("");
    setLocalSelectedProducts([]);
    setHasSearched(false);
    onClose();
  };

  // Removido carregamento automático - produtos só são carregados após aplicar filtros

  // Determinar título do modal
  const getModalTitle = () => {
    if (mode === "replacement") return "Substituir Produto";
    if (multiple) return "Selecionar Produtos";
    return "";
  };

  // Determinar se deve mostrar o botão de confirmação
  const shouldShowConfirmButton = () => {
    if (mode === "replacement") return true;
    if (multiple) return true;
    return false; // Modo search único não precisa de botão
  };

  // Determinar texto do botão de confirmação
  const getConfirmButtonText = () => {
    if (mode === "replacement") return "Confirmar Substituição";
    if (multiple) return `Confirmar Seleção (${localSelectedProducts.length})`;
    return "";
  };

  // Verificar se botão de confirmação deve estar desabilitado
  const isConfirmButtonDisabled = () => {
    if (mode === "replacement") return localSelectedProducts.length === 0;
    if (multiple) return localSelectedProducts.length === 0;
    return false;
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modal-content modal-content-cart"
      overlayClassName={{
        base: "modal modal-products-ia",
        afterOpen: "after-open",
        beforeClose: "before-close",
      }}
      closeTimeoutMS={500}
    >
      <div className="modal-header">
        <h2>{getModalTitle()}</h2>
        <button onClick={handleClose} className="close-button">
          <X size={24} />
        </button>
      </div>

      <div className="modal-body">
        {/* Informações do produto original (apenas para modo substituição) */}
        {mode === "replacement" && originalProduct && (
          <div className="original-product-info">
            <h4>Produto Original:</h4>
            <p>
              <strong>{originalProduct.product_name}</strong>
            </p>
            <p>Código: {originalProduct.product_code}</p>
            <p>
              Preço: R${" "}
              {originalProduct.product_price.toFixed(2).replace(".", ",")}
            </p>
          </div>
        )}

        <div className="search-section">
          <h4>
            {mode === "replacement"
              ? "Selecione os produtos substitutos:"
              : "Selecione os produtos:"}
          </h4>

          <div className="search-filters">
            <div className="filters-row">
              <Input
                id="search-products"
                label="Buscar produtos"
                type="text"
                placeholder="Buscar produtos..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) =>
                  e.key === "Enter" && handleSearch()
                }
                error=""
                required={false}
                purple={false}
                multiline={false}
                onFocus={() => undefined}
                onBlur={() => undefined}
                mask=""
                maskChar=""
                autocomplete="off"
                small
                readOnly={false}
                maxLength={undefined}
                min={undefined}
                description=""
              />

              <Select
                id="category-filter"
                label="Categoria"
                value={filters.category}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  handleFilterChange("subcategory", "");
                  handleFilterChange("category", e.target.value);
                }}
                options={
                  categoriesOpts?.map((cat) => ({
                    id: cat.id.toString(),
                    name: cat.category,
                  })) || []
                }
                error=""
                required={false}
                purple={false}
                placeholder=""
                optionName="name"
                small
                readOnly={!categoriesOpts?.length}
              />

              {subcategoriesOpts && subcategoriesOpts?.length >= 1 && (
                <Select
                  id="subcategory-filter"
                  label="Subcategoria"
                  value={filters.subcategory}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleFilterChange("subcategory", e.target.value)
                  }
                  options={
                    subcategoriesOpts?.map((cat) => ({
                      id: cat.id.toString(),
                      name: cat.subcategory,
                    })) || []
                  }
                  error=""
                  required={false}
                  purple={false}
                  placeholder="Subcategoria"
                  optionName="name"
                  small
                  readOnly={false}
                />
              )}

              <Button onClick={clearFilters} outlined>
                Limpar
              </Button>
              <Button onClick={handleSearch}>
                <Search size={16} />
                Buscar
              </Button>
            </div>
          </div>
        </div>

        <div className="products-list">
          {(() => {
            if (loadingProducts) {
              return <LoadingText text="Carregando produtos..." />;
            }
            if (!hasSearched) {
              return (
                <div className="no-search-message text-center p-4">
                  {/* Área vazia quando não há busca - sem mensagem ou botão */}
                </div>
              );
            }
            return (
              <div className="products-table">
                <table>
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Nome</th>
                      <th>Preço</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productsList && productsList?.length >= 1 ? (
                      productsList?.map((product) => {
                        const hasPrice =
                          ("default_partner" in product &&
                            product.default_partner?.price) ||
                          (product.partners &&
                            product.partners.length > 0 &&
                            product.partners[0].price);

                        const productPrice = (() => {
                          if (
                            "default_partner" in product &&
                            product.default_partner?.price
                          ) {
                            return product.default_partner.price;
                          }
                          if (product.partners && product.partners.length > 0) {
                            return product.partners[0].price;
                          }
                          return 0;
                        })();

                        const productStatus =
                          product.status?.status || "Sem status";
                        const isAvailable =
                          hasPrice &&
                          productStatus.toLowerCase() !== "indisponível";

                        return (
                          <tr
                            key={product.id}
                            className={`product-row ${
                              isProductSelected(product.id) ? "selected" : ""
                            } ${isProductInCart(product.id) ? "in-cart" : ""} ${
                              !isAvailable ? "unavailable" : ""
                            }`}
                            onClick={() =>
                              isAvailable && handleProductSelect(product)
                            }
                          >
                            <td className="product-code">{product.sku}</td>
                            <td className="product-name">
                              {product.product}
                              {isProductInCart(product.id) && (
                                <span className="in-cart-badge">
                                  No carrinho
                                </span>
                              )}
                            </td>
                            <td className="product-price">
                              {hasPrice ? (
                                `R$ ${productPrice
                                  .toFixed(2)
                                  .replace(".", ",")}`
                              ) : (
                                <span className="no-price-text">Sem preço</span>
                              )}
                            </td>
                            <td className="product-status">
                              <span className="status-badge">
                                {product.status?.status || "Sem status"}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center">
                          Nenhum resultado encontrado
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>

        {/* Produtos selecionados */}
        {localSelectedProducts.length > 0 && (
          <div className="selected-products">
            <h4>Produtos selecionados ({localSelectedProducts.length}):</h4>
            <div className="selected-list">
              {localSelectedProducts.map((product) => (
                <div key={product.id} className="selected-item">
                  <span>{product.product}</span>
                  <button
                    onClick={() => handleProductSelect(product)}
                    className="remove-selected"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {hasSearched &&
        productsPagination &&
        productsPagination.total_pages &&
        productsPagination.total_pages > 1 ? (
          <div className="pagination-container text-center mt-3">
            <Button
              outlined
              disabled={pageSelected === 1}
              onClick={() => fetchNewPage(pageSelected - 1)}
            >
              Anterior
            </Button>
            <span className="page-info mx-3">
              Página {pageSelected} de {productsPagination.total_pages}
            </span>
            <Button
              outlined
              disabled={pageSelected === productsPagination.total_pages}
              onClick={() => fetchNewPage(pageSelected + 1)}
            >
              Próxima
            </Button>
          </div>
        ) : null}
      </div>

      <div className="modal-footer">
        <Button onClick={handleClose} outlined>
          Cancelar
        </Button>
        {shouldShowConfirmButton() && (
          <Button
            onClick={() => {
              if (mode === "replacement") {
                handleConfirmReplacement();
              } else if (multiple) {
                handleConfirmMultipleSelection();
              }
            }}
            disabled={isConfirmButtonDisabled()}
          >
            {getConfirmButtonText()}
          </Button>
        )}
      </div>
    </Modal>
  );
};
